import { useParams } from 'react-router';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IWarningRatification } from '../../../../../../../models/interfaces/WarningRatification';
import { useProcessing } from '../../../../../../../hooks/useProcessing';
import { useActions } from '../../../../../../../hooks/useActions';
import { updateModalState } from '../../../../../../../store/redux-slices/modals';
import { SUBSCRIPTION_DETAILS_EDITING_PROCESSING } from '../../../../../../../store/redux-slices/processes/constants';
import { organizationNameSelector } from '../../../../../../../store/redux-slices/organizations/selectors';
import { useFormErrorsActions } from '../../../../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

import { useApiResponseFail } from '../../../../../../../hooks/useApiResponseFail';

import { editDOCSubscriptionOptions } from '../../../../../../../store/saga-slices/subscriptions/dutyOfCare';
import { docSubscriptionOverviewSelector } from '../../../../../../../store/redux-slices/subscriptions/dutyOfCare/selectors';
import { IDOCOrganizationSubscriptionOptionsModel } from '../../../../../../../models/interfaces/Subscription/DutyOfCare/DOCOrganizationSubscriptionOptionsModel';
import { IEditDOCSubscriptionOptionsPayload } from '../../../../../../../store/saga-slices/subscriptions/dutyOfCare/models';
import { DutyOfCareSubscriptionOptions } from '../../../../../../../models/enums/DutyOfCareSubscriptionOptions';
import { EDITING_DOC_SUBSCRIPTION_OPTIONS_MODAL_NAME } from './DOCSubscriptionEditOptionsModal.const';

type UseDOCSubscriptionEditDetailsModalType = {
  onModalSuccess: () => void;
};

export const useDOCSubscriptionEditDetailsModal = ({
  onModalSuccess,
}: UseDOCSubscriptionEditDetailsModalType) => {
  const alert = useAlert();
  const { t } = useTranslation();

  const UpdateModalState = useActions(updateModalState);
  const EditSubscriptionDetails = useActions(editDOCSubscriptionOptions);

  const subscriptionEditing = useProcessing(
    SUBSCRIPTION_DETAILS_EDITING_PROCESSING,
  );

  const subscriptionOverview = useSelector(docSubscriptionOverviewSelector);
  const organizationName = useSelector(organizationNameSelector);

  const { id: organizationId, subscriptionId } = useParams();

  const { validateErrors } = useFormErrorsActions();
  const { handleResponse } = useApiResponseFail();

  const handleSubmit = (values: IDOCOrganizationSubscriptionOptionsModel) => {
    editOrganizationSubscription(values);
  };

  const editOrganizationSubscription = (
    values: IDOCOrganizationSubscriptionOptionsModel,
    ratifications: IWarningRatification[] = [],
  ) => {
    const payload: IEditDOCSubscriptionOptionsPayload = {
      organizationId,
      subscriptionId,
      data: values,
      success: () => {
        alert.success(
          t('alerts.subscription-edit-success', {
            value: organizationName,
          }),
        );
        onModalSuccess && onModalSuccess();
        UpdateModalState(EDITING_DOC_SUBSCRIPTION_OPTIONS_MODAL_NAME);
      },
      error: (err) => {
        handleResponse({
          err,
          values,
          ratifications,
          alertErrorMessage: t('alerts.subscription-edit-fail', {
            value: organizationName,
          }),
        });
      },
    };
    EditSubscriptionDetails(payload);
  };

  const handleWarningsConfirm = (
    data: IDOCOrganizationSubscriptionOptionsModel,
    warnings: IWarningRatification[],
  ) => {
    UpdateModalState([EDITING_DOC_SUBSCRIPTION_OPTIONS_MODAL_NAME, { data }]);
    editOrganizationSubscription(data, warnings);
  };

  const onCancelHandler = () =>
    UpdateModalState(EDITING_DOC_SUBSCRIPTION_OPTIONS_MODAL_NAME);

  const initData = subscriptionOverview && {
    [DutyOfCareSubscriptionOptions.AutoRenewal]:
      subscriptionOverview.autoRenewal,
  };

  return {
    handleWarningsConfirm,
    handleSubmit,
    validateErrors,
    UpdateModalState,
    subscriptionEditing,
    initData,
    onCancelHandler,
  };
};
